import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import SeatSelectorComponent from 'components/purchase/SeatSelector';
import MultiTripSeatSelector from 'components/purchase/MultiTripSeatSelector';
import Loading from 'components/Loading';
import { exchangeSeatChangeTracker, seatChangeTracker } from 'metrics/user-analytics/purchase';
import { validateAndRedirectSeatSelection } from 'utils/seatValidation';

const propTypes = {
  purchase: PropTypes.shape({
    isExchange: PropTypes.bool.isRequired,
    hasDetails: PropTypes.bool.isRequired,
    token: PropTypes.string.isRequired,
    isFetchingDetails: PropTypes.bool.isRequired,
    loaded: PropTypes.bool.isRequired,
    departs: PropTypes.object.isRequired,
    passengers: PropTypes.arrayOf(
      PropTypes.shape({
        category: PropTypes.string.isRequired,
      }),
    ).isRequired,
    returns: PropTypes.object.isRequired,
    allowsSeatSelection: PropTypes.bool.isRequired,
    isUpdating: PropTypes.bool.isRequired,
    roundTrip: PropTypes.bool.isRequired,
  }),
  fetchTripsDetails: PropTypes.func.isRequired,
  selectSeats: PropTypes.func.isRequired,
  sendNotification: PropTypes.func.isRequired,
  next: PropTypes.func.isRequired,
  goToDepartureSeats: PropTypes.func.isRequired,
  updateSeat: PropTypes.func.isRequired,
  allowsSeatSelection: PropTypes.bool.isRequired,
  departureFragments: PropTypes.array.isRequired,
  returnFragments: PropTypes.array.isRequired,
  hasDetails: PropTypes.bool.isRequired,
  isFetchingDetails: PropTypes.bool.isRequired,
  passengers: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isExchange: PropTypes.bool.isRequired,
  isLockingTickets: PropTypes.bool,
  fetchingDetails: PropTypes.bool,
  selectedSeats: PropTypes.array,
  layout: PropTypes.object,
  seats: PropTypes.array,
  diagramType: PropTypes.string,
  isPetFriendlyTrip: PropTypes.bool,
  lockedSeats: PropTypes.array,
  isLoading: PropTypes.bool,
  isOpenTicket: PropTypes.bool,
  minPassengers: PropTypes.number,
  canGoBack: PropTypes.bool,
  canResetPassengers: PropTypes.bool,
  resetPassengers: PropTypes.func.isRequired,
  isPurchaseView: PropTypes.bool,
  tripPricing: PropTypes.object,
  hasSeats: PropTypes.bool,
  tripId: PropTypes.string,
  resultsLayout: PropTypes.bool,
  onClose: PropTypes.func,
  trip: PropTypes.object,
  clearError: PropTypes.func,
  fetchingDetailsError: PropTypes.bool,
  t: PropTypes.func.isRequired,
  passengerTypes: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
    }),
  ),
  firstFloorReclination: PropTypes.string,
  secondFloorReclination: PropTypes.string,
  history: PropTypes.object,
  overbooking: PropTypes.bool,
  onQuickPurchaseFromSeats: PropTypes.func,
  frequentPassengersCount: PropTypes.number,
  bookingPassengerCount: PropTypes.number,
  seatPreferenceDiagramType: PropTypes.oneOf(['bus', 'van', 'unknown']),
  useBfoPurchaseScope: PropTypes.bool,
  usePerLegBfoDetails: PropTypes.bool,
  pricingVariant: PropTypes.string,
};

/**
 * Represents the ReturnSeats component.
 */
class ReturnSeats extends Component {
  constructor(props) {
    super(props);

    this.finishedSeatSelection = this.finishedSeatSelection.bind(this);
  }

  componentDidMount() {
    const {
      allowsSeatSelection,
      canResetPassengers,
      resetPassengers,
      passengers,
      isPurchaseView,
      purchase,
      history,
    } = this.props;

    if (isPurchaseView) {
      validateAndRedirectSeatSelection(purchase, {
        redirectFn: (path) => history.replace(path),
        type: 'returns',
      });
    }

    this.getReservationDetails();
    if (!isPurchaseView) return;

    if (canResetPassengers) {
      resetPassengers(passengers);
    }

    if (!allowsSeatSelection) {
      this.finishedSeatSelection();
    }
  }

  getReservationDetails() {
    const {
      fetchTripsDetails,
      departureFragments,
      returnFragments,
      hasDetails,
      isFetchingDetails,
      useBfoPurchaseScope,
      usePerLegBfoDetails,
      purchase,
      isExchange,
      isPurchaseView,
      pricingVariant,
    } = this.props;

    if (!(hasDetails || isFetchingDetails)) {
      // BFO purchases load their seat map in the scope of the purchase (RM-1201).
      const perLegRequest = usePerLegBfoDetails
        ? { departureFragments: [], returnFragments, ways: ['returns'] }
        : { departureFragments, returnFragments };
      // A pre-purchase canonical BFO round-trip return leg asks Platform Purchase for its
      // own pricing variant, independently of the departure request.
      const resultRequestOptions =
        !isPurchaseView && pricingVariant
          ? { requestOptionsByWay: { returns: { pricingVariant } } }
          : {};
      fetchTripsDetails({
        ...perLegRequest,
        ...((isExchange || useBfoPurchaseScope) && { purchaseToken: purchase.token }),
        ...resultRequestOptions,
      });
    }
  }

  finishedSeatSelection() {
    const { purchase, next, isExchange, resultsLayout } = this.props;

    if (isExchange) {
      exchangeSeatChangeTracker(purchase, 'return', resultsLayout ? 'results' : 'purchase');
    } else {
      seatChangeTracker(purchase, 'return', resultsLayout ? 'results' : 'purchase');
    }

    next();
  }

  render() {
    const {
      selectSeats,
      sendNotification,
      goToDepartureSeats,
      updateSeat,
      passengers,
      diagramType,
      fetchingDetails,
      isLockingTickets,
      selectedSeats = [],
      layout,
      seats,
      lockedSeats,
      isLoading,
      isPetFriendlyTrip,
      isOpenTicket,
      allowsSeatSelection,
      minPassengers,
      canGoBack,
      tripPricing,
      hasSeats,
      tripId,
      resultsLayout,
      onClose,
      trip,
      clearError,
      fetchingDetailsError,
      purchase,
      t,
      passengerTypes,
      firstFloorReclination,
      secondFloorReclination,
      overbooking,
      onQuickPurchaseFromSeats,
      frequentPassengersCount,
      bookingPassengerCount,
      seatPreferenceDiagramType,
    } = this.props;

    const busPassengers = passengers?.filter((p) => p.category !== 'infant');
    const loading = !allowsSeatSelection || isLockingTickets || !hasSeats || isLoading;

    if (loading && !resultsLayout) return <Loading loadingText={t('seats:loading')} />;

    if (purchase?.returns?.hasConnections) {
      return (
        <MultiTripSeatSelector
          way="return"
          selectSeats={selectSeats}
          finishSeatSelection={this.finishedSeatSelection}
        />
      );
    }

    return (
      <SeatSelectorComponent
        tripId={tripId}
        allowsSeatSelection={allowsSeatSelection}
        diagramType={diagramType}
        way="return"
        fetchingDetails={fetchingDetails}
        isPetFriendly={isPetFriendlyTrip}
        layout={layout}
        passengers={busPassengers}
        selectedSeats={selectedSeats}
        lockedSeats={lockedSeats}
        selectSeats={selectSeats}
        sendNotification={sendNotification}
        onFinishedSelection={this.finishedSeatSelection}
        onBackAction={goToDepartureSeats}
        isOpenTicket={isOpenTicket}
        seats={seats}
        minPassengers={minPassengers}
        updateSeat={updateSeat}
        isLoading={loading}
        canGoBack={canGoBack}
        tripPricing={tripPricing}
        passengerTypes={passengerTypes}
        resultsLayout={resultsLayout}
        onClose={onClose}
        hasSeats={hasSeats}
        trip={trip}
        clearError={clearError}
        fetchingDetailsError={fetchingDetailsError}
        firstFloorReclination={firstFloorReclination}
        secondFloorReclination={secondFloorReclination}
        roundTrip={purchase?.roundTrip}
        overbooking={overbooking}
        onQuickPurchaseFromSeats={onQuickPurchaseFromSeats}
        frequentPassengersCount={frequentPassengersCount}
        bookingPassengerCount={bookingPassengerCount}
        seatPreferenceDiagramType={seatPreferenceDiagramType}
      />
    );
  }
}

ReturnSeats.propTypes = propTypes;

export default withTranslation('')(ReturnSeats);
